import React from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography';
import { ticketColors } from '../../../shared/theme/ticketColors';
import type {
  OtherFeatureGroup,
  OtherFeatureOptionId,
} from '../../../onboarding/application/otherFeatureCatalog';
import { hapticTap } from '../../../shared/ui/haptics';
import type { OnboardingLayout } from './useOnboardingLayout';

type Props = {
  layout: OnboardingLayout;
  featureGroups: readonly OtherFeatureGroup[];
  selectedFeatureIds: readonly OtherFeatureOptionId[];
  onToggleFeature: (id: OtherFeatureOptionId) => void;
  verticalLabel: string;
  onChangeVerticalLabel: (value: string) => void;
  labelInputRef?: React.RefObject<TextInput | null>;
  disabled?: boolean;
};

export function OtherRubroStepPanel({
  layout,
  featureGroups,
  selectedFeatureIds,
  onToggleFeature,
  verticalLabel,
  onChangeVerticalLabel,
  labelInputRef,
  disabled = false,
}: Props) {
  const selected = new Set(selectedFeatureIds);

  return (
    <View style={styles.root} testID="onboarding-other-rubro-step">
      <Text style={styles.sectionLabel}>¿Qué vendés?</Text>
      <Text style={styles.fieldHint}>En pocas palabras, para que te entendamos.</Text>
      <TextInput
        ref={labelInputRef}
        style={[styles.input, styles.textarea, layout.isWide && styles.inputWide]}
        value={verticalLabel}
        onChangeText={onChangeVerticalLabel}
        placeholder="Ej. artículos de pesca y camping"
        placeholderTextColor={ticketColors.ink3}
        editable={!disabled}
        maxLength={120}
        multiline
        testID="other-rubro-vertical-label"
        accessibilityLabel="¿Qué vendés?"
      />

      <Text style={styles.featuresIntro}>¿Qué necesitás en el local?</Text>
      <Text style={styles.fieldHint}>Podés marcar varias.</Text>

      {featureGroups.map((group) => (
        <View key={group.id} style={styles.groupBlock}>
          <Text style={styles.groupLabel}>{group.label}</Text>
          <View style={styles.featList}>
            {group.options.map((option) => {
              const isOn = selected.has(option.id);
              return (
                <TouchableOpacity
                  key={option.id}
                  style={[styles.featRow, isOn && styles.featRowSelected]}
                  onPress={() => {
                    hapticTap();
                    onToggleFeature(option.id);
                  }}
                  disabled={disabled}
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: isOn }}
                  testID={`other-feature-${option.id}`}
                >
                  <View style={[styles.box, isOn && styles.boxOn]}>{isOn ? <Text style={styles.boxCheck}>✓</Text> : null}</View>
                  <View style={styles.featText}>
                    <Text style={[styles.featLabel, isOn && styles.featLabelOn]}>{option.label}</Text>
                    {option.hint ? <Text style={styles.featHint}>{option.hint}</Text> : null}
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    gap: spacing.sm,
  },
  sectionLabel: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
    fontSize: 15,
  },
  featuresIntro: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
    fontSize: 15,
    marginTop: spacing.md,
  },
  fieldHint: {
    ...typography.caption,
    color: ticketColors.ink3,
    marginBottom: spacing.xs,
  },
  groupBlock: {
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  groupLabel: {
    ...typography.caption,
    color: ticketColors.gold,
    fontWeight: '700',
    fontSize: 11,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  featList: {
    gap: spacing.sm,
  },
  featRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    borderWidth: 1.5,
    borderColor: 'rgba(15, 23, 42, 0.14)',
    borderRadius: 14,
    backgroundColor: '#FFFFFF',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
  },
  featRowSelected: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
    borderWidth: 2,
  },
  box: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: 'rgba(15, 23, 42, 0.22)',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
    flexShrink: 0,
    backgroundColor: '#FFFFFF',
  },
  boxOn: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold,
  },
  boxCheck: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '700',
  },
  featText: {
    flex: 1,
    gap: 2,
  },
  featLabel: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  featLabelOn: {
    color: ticketColors.gold,
  },
  featHint: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  input: {
    borderWidth: 1.5,
    borderColor: ticketColors.warnBorder,
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    ...typography.body,
    color: ticketColors.ink,
    minHeight: 48,
  },
  textarea: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  inputWide: {
    maxWidth: 480,
  },
});
